import { type FC, type PropsWithChildren, useEffect, useMemo, useRef } from 'react';
import { useUnit } from 'effector-react';
import { ResponsiveModal } from '../ResponsiveModal';
import {
    $responsiveModals,
    responsiveModalClosed,
    responsiveModalDiscarded,
    responsiveModalUpdated,
    type ResponsiveContextModalProps,
    type ResponsiveModalsMap,
} from './model';

interface ResponsiveModalsProviderProps extends PropsWithChildren {
    modals: ResponsiveModalsMap;
}

const noop = () => undefined;
const focusableSelector =
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const ResponsiveModalsProvider = ({ children, modals }: ResponsiveModalsProviderProps) => {
    const { entries } = useUnit($responsiveModals);
    const closeModal = useUnit(responsiveModalClosed);
    const discardModal = useUnit(responsiveModalDiscarded);
    const updateModal = useUnit(responsiveModalUpdated);
    const layerNodes = useRef(new Map<number, HTMLDivElement>());

    const { invalidEntries, validEntries } = useMemo(
        () =>
            entries.reduce(
                (result, entry) => {
                    if (Object.hasOwn(modals, entry.modal)) {
                        result.validEntries.push(entry);
                    } else {
                        result.invalidEntries.push(entry);
                    }

                    return result;
                },
                { invalidEntries: [] as typeof entries, validEntries: [] as typeof entries },
            ),
        [entries, modals],
    );
    const validEntryCount = validEntries.length;
    const topValidOpenId = validEntries.at(-1)?.openId;
    const previousValidEntryCount = useRef(validEntryCount);
    const rootReturnTarget = useRef<HTMLElement | null>(null);

    useEffect(() => {
        invalidEntries.forEach((entry) => {
            if (import.meta.env.DEV) {
                // eslint-disable-next-line no-console -- Unknown registry entries must be visible during development.
                console.error(`Unknown responsive modal: ${entry.modal}`);
            }

            discardModal(entry.openId);
        });
    }, [discardModal, invalidEntries]);

    useEffect(() => {
        const previousCount = previousValidEntryCount.current;

        previousValidEntryCount.current = validEntryCount;

        if (previousCount === 0 && validEntryCount > 0) {
            rootReturnTarget.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;

            return undefined;
        }

        if (previousCount > 0 && validEntryCount === 0) {
            const returnTarget = rootReturnTarget.current;

            rootReturnTarget.current = null;

            if (!returnTarget?.isConnected) {
                return undefined;
            }

            const focusTimer = window.setTimeout(() => {
                if (returnTarget.isConnected) {
                    returnTarget.focus({ preventScroll: true });
                }
            });

            return () => window.clearTimeout(focusTimer);
        }

        if (previousCount > validEntryCount && validEntryCount > 0) {
            if (topValidOpenId === undefined) {
                return undefined;
            }

            const focusTimer = window.setTimeout(() => {
                const layer = layerNodes.current.get(topValidOpenId);

                if (layer) {
                    const focusTarget = layer.querySelector<HTMLElement>(focusableSelector) ?? layer;

                    focusTarget.focus();
                }
            });

            return () => window.clearTimeout(focusTimer);
        }

        return undefined;
    }, [validEntryCount, topValidOpenId]);

    const context = useMemo(() => ({ closeModal, updateModal }), [closeModal, updateModal]);

    return (
        <>
            {children}

            {validEntries.map((entry, index) => {
                const ModalComponent = modals[entry.modal] as FC<ResponsiveContextModalProps<object>>;
                const topmost = index === validEntries.length - 1;

                return (
                    <ResponsiveModal
                        key={entry.openId}
                        opened
                        stackIndex={index}
                        topmost={topmost}
                        title={entry.title}
                        drawerProps={entry.drawerProps}
                        modalProps={entry.modalProps}
                        onClose={topmost ? () => closeModal(entry.modalId) : noop}
                    >
                        <div
                            ref={(node) => {
                                if (node) {
                                    layerNodes.current.set(entry.openId, node);
                                } else {
                                    layerNodes.current.delete(entry.openId);
                                }
                            }}
                            tabIndex={-1}
                        >
                            <ModalComponent
                                context={context}
                                id={entry.modalId}
                                innerProps={entry.innerProps}
                            />
                        </div>
                    </ResponsiveModal>
                );
            })}
        </>
    );
};
